import { useAuth0 } from '@auth0/auth0-react';

import { Tabs, TabsList, TabsTrigger } from '@base/components/ui/tabs';
import Avatar from '@basic/Avatar';
import Loader from '@basic/Loader';
import GoalCardsGrid from '@components/goals/GoalCardsGrid';
import GoalRecommendationBanner from '@components/goals/GoalRecommendationBanner';
import NoSubscriptionsComponent from '@components/shared/NoSubscriptionsComponent';
import ScrollableArea from '@components/shared/ScrollableArea';
import PackagesFilterRow from '@components/subscriptions/PackagesFilterRow';
import PackagesSection from '@components/subscriptions/PackagesSection';
import { GoalComponentsVisibility, GoalViewName, UIPaths } from '@consts';
import { useAccountsService } from '@hooks/useAccountsService';
import { useConversationsService } from '@hooks/useConversationsService';
import { useEventsListener } from '@hooks/useEventsListener';
import {
  GoalSubscriptionAndEnrollableGoalsModel,
  useGoalSubscriptionAndEnrollableGoalsService
} from '@hooks/useGoalSubscriptionAndEnrollableGoalsService';
import { useGoalSubscriptionService } from '@hooks/useGoalSubscriptionService';
import { LogoIcon, MessageSquareIcon, MountainWithFlagIcon, NotificationIcon } from '@icons';
import { MasterLink } from '@master';
import { GoalSubscriptionStatus, GoalSubscriptionsSummaryModel } from '@models';
import * as Sentry from '@sentry/nextjs';
import { splitGoalsByPackages } from '@services/splitGoalsByPackages';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import * as _ from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const HomeWelcomeText = ({ name }: { name?: string | null }) => {
  const { t } = useTranslation();
  return (
    <div className='self-stretch px-xs py-sm inline-flex flex-col justify-start items-center gap-sm mb-md'>
      <div className='w-full max-w-[800px] px-xs inline-flex justify-start items-start gap-xs'>
        <div className='flex-1 inline-flex flex-col justify-start items-start'>
          <div className='self-stretch p-sm inline-flex justify-start items-center gap-sm'>
            <div className='w-8 h-18 relative'>
              <LogoIcon size={42} backgroundColor='transparent' animateOnMount />
            </div>
            <div
              className={classNames('text-center justify-start P1B', setupForDark('text-primary'))}
            >
              {t('pages.home.welcome', { name })}
            </div>
          </div>
          <div className='self-stretch p-sm inline-flex justify-start items-center gap-sm'>
            <div className={classNames('flex-1 justify-start P2R', setupForDark('text-primary'))}>
              {t('pages.home.welcomeSubtext')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TAB_ACTIVE_GOALS = 'TAB_ACTIVE_GOALS';
const TAB_MESSAGES = 'TAB_MESSAGES';

const HomeTabsDesktop = ({
  yourGoalsCount = 0,
  messagesCount = 0,
  activeTab,
  onTabChange
}: {
  yourGoalsCount?: number;
  messagesCount?: number;
  activeTab: string;
  onTabChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className='w-full max-w-[800px]'>
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className='mb-sm sm:mb-xxl'>
          <TabsTrigger
            value={TAB_ACTIVE_GOALS}
            icon={<MountainWithFlagIcon size={24} />}
            label={t('pages.home.yourGoalsTab', { count: yourGoalsCount })}
          />
          <TabsTrigger
            value={TAB_MESSAGES}
            icon={<MessageSquareIcon size={24} />}
            label={t('pages.home.messagesTab', { count: messagesCount })}
          />
        </TabsList>
      </Tabs>
    </div>
  );
};

const renderGraphicWithActionButtonPlaceholder = (
  router,
  t,
  goalSubscriptionsSummary: GoalSubscriptionsSummaryModel
) => {
  const summary = goalSubscriptionsSummary;
  if (_.isNil(summary) || _.isNil(summary.statusCounts)) {
    return null;
  }
  const activeCount = summary.statusCounts.find(
    (status) => status.status === GoalSubscriptionStatus.ACTIVE
  )?.count;
  const pausedCount = summary.statusCounts.find(
    (status) => status.status === GoalSubscriptionStatus.PAUSED
  )?.count;
  const totalCount = summary.total;

  if (totalCount === 0) {
    return (
      <NoSubscriptionsComponent
        animated
        header={t('pages.home.neverHadActiveGoalSubscriptions.header')}
        headerVariant='primary'
        text={t('pages.home.neverHadActiveGoalSubscriptions.text')}
        buttonText={t('pages.home.neverHadActiveGoalSubscriptions.button')}
        buttonOnClick={() => router.push(UIPaths.explore())}
      />
    );
  } else if (activeCount === 0 && totalCount > 0 && pausedCount === 0) {
    return (
      <NoSubscriptionsComponent
        animated
        header={t('pages.home.noActiveGoalSubscriptions.header')}
        headerVariant='primary'
        buttonText={t('pages.home.noActiveGoalSubscriptions.button')}
        buttonOnClick={() => router.push(UIPaths.explore())}
      />
    );
  } else if (pausedCount > 0 && activeCount === 0) {
    return (
      <NoSubscriptionsComponent
        animated
        header={t('pages.home.goalSubscriptionsArePaused.header')}
        text={t('pages.home.goalSubscriptionsArePaused.text')}
        buttonText={t('pages.home.goalSubscriptionsArePaused.button')}
        buttonOnClick={() => router.push(UIPaths.goalSubscriptions())}
      />
    );
  } else {
    // NOTE: By default we show the explore
    return (
      <NoSubscriptionsComponent
        animated
        header={t('pages.home.neverHadActiveGoalSubscriptions.header')}
        headerVariant='primary'
        text={t('pages.home.neverHadActiveGoalSubscriptions.text')}
        buttonText={t('pages.home.neverHadActiveGoalSubscriptions.button')}
        buttonOnClick={() => router.push(UIPaths.explore())}
      />
    );
  }
};

function Inbox(): ReactElement {
  const { t } = useTranslation();
  useEventsListener();
  const [goalSubscriptionsSummary, setGoalSubscriptionsSummary] =
    useState<GoalSubscriptionsSummaryModel | null>(null);
  const [isPendingGetGoalSubscriptionsSummary, setIsPendingGetGoalSubscriptionsSummary] =
    useState(true);

  const { getGoalSubscriptionsSummary } = useGoalSubscriptionService();
  const { getInboxSummaries } = useConversationsService();
  const { accountMe } = useAccountsService();
  const [messagesCount, setMessagesCount] = useState(0);
  const { initIntroIsDone } = useStore((state) => ({
    initIntroIsDone: state.initIntroIsDone
  }));
  const router = useRouter();
  const { user } = useAuth0();
  const [activeTab, setActiveTab] = useState(TAB_ACTIVE_GOALS);

  const handleTabChange = (tab: string) => {
    if (tab === TAB_MESSAGES) {
      router.push(UIPaths.messages());
      return;
    }
    setActiveTab(tab);
  };
  const [subscriptionsAndEnrollableGoals, setSubscriptionsAndEnrollableGoals] =
    useState<GoalSubscriptionAndEnrollableGoalsModel>({
      goalSubscriptions: [],
      enrollableGoals: [],
      packages: []
    });
  const [showPackages, setShowPackages] = useState(true);

  const { subscriptionsOutsidePackages, enrollableGoalsOutsidePackages, packageBuckets } =
    splitGoalsByPackages(subscriptionsAndEnrollableGoals, showPackages);

  const { isLoading: isLoadingSubscriptionsAndGoals, getGoalSubscriptionsAndEnrollableGoals } =
    useGoalSubscriptionAndEnrollableGoalsService();

  // Initialize localStorage state
  useEffect(() => {
    initIntroIsDone();
  }, [initIntroIsDone]);

  useEffect(() => {
    // Fetch summary for placeholder logic
    setIsPendingGetGoalSubscriptionsSummary(true);
    getGoalSubscriptionsSummary()
      .then((response) => {
        setGoalSubscriptionsSummary(response);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setGoalSubscriptionsSummary(null);
      })
      .finally(() => {
        setIsPendingGetGoalSubscriptionsSummary(false);
      });

    // Fetch subscriptions and enrollable goals
    getGoalSubscriptionsAndEnrollableGoals(undefined, [GoalSubscriptionStatus.ACTIVE]).then(
      (result) => {
        setSubscriptionsAndEnrollableGoals(result);
      }
    );

    getInboxSummaries({ includeCleaned: true, size: 1 })
      .then((response) => {
        setMessagesCount(response.notDoneTotal);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setMessagesCount(0);
      });
  }, []);

  const isLoading = isPendingGetGoalSubscriptionsSummary || isLoadingSubscriptionsAndGoals;
  const activeGoalSubscriptions = subscriptionsAndEnrollableGoals.goalSubscriptions || [];
  const hasActiveGoalsOrEnrollable =
    activeGoalSubscriptions.length > 0 ||
    subscriptionsAndEnrollableGoals.enrollableGoals.length > 0;

  return (
    <>
      <div
        className={classNames(
          'flex',
          'flex-col',
          'h-full',
          'gap-sm',
          'overflow-hidden',
          'w-full',
          !isLoading ? '' : 'max-w-[800px] mx-auto'
        )}
      >
        <GoalRecommendationBanner />
        <div className={classNames('block sm:hidden')}>
          <div className={classNames('mx-md mt-md mb-xs flex justify-between items-center gap-md')}>
            <MasterLink />
            <div className='flex flex-row items-center justify-end gap-md'>
              <Link href={UIPaths.notifications()}>
                <NotificationIcon
                  size={32}
                  className={classNames(setupForDark('text-icon-secondary'))}
                />
              </Link>
              <Link href={UIPaths.accountMe(undefined, router.asPath)}>
                <Avatar src={accountMe.user.pictureUrl} size={32} />
              </Link>
            </div>
          </div>
        </div>
        <ScrollableArea>
          <HomeWelcomeText name={user?.given_name || ''} />
          <div
            className={classNames(
              'w-full flex justify-center px-xxs sticky top-0 z-20',
              setupForDark('bg-page')
            )}
          >
            <HomeTabsDesktop
              yourGoalsCount={
                activeGoalSubscriptions.length +
                subscriptionsAndEnrollableGoals.enrollableGoals.length
              }
              messagesCount={messagesCount}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>

          {/* CONTENT BASED ON ACTIVE TAB */}
          {activeTab === TAB_ACTIVE_GOALS && (
            <>
              {isLoading ? (
                <Loader />
              ) : hasActiveGoalsOrEnrollable ? (
                <>
                  {subscriptionsAndEnrollableGoals.packages.length > 0 && (
                    <PackagesFilterRow
                      showPackages={showPackages}
                      onShowPackagesChange={setShowPackages}
                    />
                  )}
                  {subscriptionsOutsidePackages.length + enrollableGoalsOutsidePackages.length >
                    0 && (
                    <GoalCardsGrid
                      subscriptions={subscriptionsOutsidePackages}
                      goals={enrollableGoalsOutsidePackages}
                      showActions={GoalComponentsVisibility[GoalViewName.HOME].actions}
                    />
                  )}
                  {packageBuckets.map(({ pkg, goals }) => (
                    <PackagesSection
                      key={pkg.entityId}
                      pkg={pkg}
                      goals={goals}
                      showActions={GoalComponentsVisibility[GoalViewName.HOME].actions}
                    />
                  ))}
                </>
              ) : (
                renderGraphicWithActionButtonPlaceholder(router, t, goalSubscriptionsSummary)
              )}
            </>
          )}
        </ScrollableArea>
      </div>
    </>
  );
}

export default Inbox;
